<template>
  <div class="flex flex-col gap-3">
    <div v-if="title || subtitle?.length" class="flex flex-col gap-1">
      <h4 v-if="title" class="typo-title4 tracking-[-0.02em] text-grey-dark">
        {{ title }}
      </h4>

      <div v-if="subtitle?.length" class="flex flex-col gap-1">
        <p
          v-for="(subtitleLine, subtitleIndex) in subtitle"
          :key="`${subtitleIndex}-${subtitleLine}`"
          class="typo-text2 italic tracking-[-0.02em] text-grey-dark"
        >
          {{ subtitleLine }}
        </p>
      </div>
    </div>

    <ul v-if="items?.length" class="flex flex-col gap-1">
      <li v-for="item in items" :key="item.id">
        <SideNavItem
          v-if="!item.hidden"
          :capture-button-name="item.captureButtonName ?? `navigate-${item.id}`"
          :label="item.label"
          :icon="item.icon"
          :href="item.href"
          :active="isItemActive(item)"
          :disabled="item.disabled"
          :outside-link="item.outsideLink"
          @click="emit('item-click', item)"
        />
      </li>
    </ul>

    <slot />

    <SideNavDivider v-if="dividerAfter" />
  </div>
</template>

<script setup lang="ts">
import SideNavDivider from '@design-system/side-nav/application/component/SideNavDivider/SideNavDivider.vue';
import SideNavItem from '@design-system/side-nav/application/component/SideNavItem/SideNavItem.vue';
import type { SideNavItem as SideNavItemType } from '@design-system/side-nav/application/component/SideNav/SideNav.types';
import { toRefs } from 'vue';

interface SideNavSectionProps {
  title?: string;
  subtitle?: string[];
  items?: SideNavItemType[];
  dividerAfter?: boolean;
  activeItemId?: string | null;
}

const props = defineProps<SideNavSectionProps>();
const emit = defineEmits<{
  'item-click': [payload: SideNavItemType];
}>();

const { title, subtitle, items, dividerAfter, activeItemId } = toRefs(props);

const isItemActive = (item: SideNavItemType): boolean => {
  if (activeItemId.value) {
    return item.associatedRouteName === activeItemId.value || item.id === activeItemId.value;
  }

  return item.active ?? false;
};
</script>
